import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngrx/store';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import * as fromRoot from '../../reducers';
import * as customer from '../../actions/customer';
import * as meet from '../../actions/meet';
import { CustomersSelectedForEngage } from '../../actions/customer';
import { AnalyticsService } from '../../core/analytics';
import { CustomerService } from '../../core/customer.service';
import { UserService } from '../../core/user';
import { MeetConstants } from '../../meet/meet.constants';
import { AppConstants } from '../../app.constants';
import { SharedConstants } from '../shared.constants';
import { IconConfirmationDialogComponent } from '../icon-confirmation-dialog/icon-confirmation-dialog.component';
import { SaveGroupNameDialogComponent } from '../save-group-name-dialog/save-group-name-dialog.component';

@Component({
  selector: 'actions-menu',
  templateUrl: './actions-menu.component.html',
  styleUrls: ['./actions-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ActionsMenuComponent {
  @Input() public canMakeInactive: boolean = false;
  @Input() public selectedCustomerIds: string[] = [];
  @Input() public isMobile: boolean = false;
  @Input() public isLoading: boolean = false;
  @Input() public disableActionMenu: boolean = false;
  @Input() public unsavedChangesTitle: string = '';
  @Input() public unsavedChangesText: string = '';
  @Input() public unsavedChangesIcon: string = '';
  @Input() public unsavedChangesCancel: string = '';
  @Input() public unsavedChangesConfirm: string = '';
  @Input() public shouldDisplayUnsavedChangesDialog: boolean = false;
  @Input() public keepAppliedFilterAnalytics: boolean = false;
  @Input() public showCreateSavedGroupOption: boolean = false;

  public isAllSendingDisabled$: Observable<boolean>;
  public isRewardsSendingDisabled$: Observable<boolean>;
  public disableCreateSavedGroup$: Observable<boolean>;
  public menuOpen: boolean = false;

  constructor(private readonly store: Store<fromRoot.State>,
              private readonly router: Router,
              private readonly analytics: AnalyticsService,
              private readonly dialog: MatDialog,
              private readonly customerService: CustomerService,
              private readonly us: UserService) {
    this.isAllSendingDisabled$ = store.select(fromRoot.getFeatureEnabled(MeetConstants.disableSendingFlag));
    this.isRewardsSendingDisabled$ = store.select(fromRoot.getFeatureEnabled(SharedConstants.disableRewardSendFlag));
    this.disableCreateSavedGroup$ = store.select(fromRoot.getMeetSelectedSavedGroups).pipe(
      map((savedGroups) => (savedGroups?.length ?? 0) > 0)
    );
  }

  public createSavedGroup(): void {
    this.store.select(fromRoot.getCustomers).pipe(
      take(1),
      switchMap((customers = []) => {
        const selectedCustomers = this.selectedCustomerIds
          .map((id) => customers.find((customerProfile) => customerProfile.id === id))
          .filter(Boolean);
        const selectedCustomerIdsSet = new Set(selectedCustomers.map((customerProfile) => customerProfile.id));
        const missingIds = this.selectedCustomerIds.filter((id) => !selectedCustomerIdsSet.has(id));

        if (missingIds.length === 0) {
          return of(selectedCustomers);
        }

        return this.fetchCustomersByIds(missingIds).pipe(
          map((fetchedCustomers) => [...selectedCustomers, ...fetchedCustomers]),
          catchError(() => of(selectedCustomers))
        );
      })
    ).subscribe((customers = []) => {
      const includeCustomers = this.selectedCustomerIds.map((id) => {
        const selectedCustomer = customers.find((customerProfile) => customerProfile.id === id);
        const fullName = selectedCustomer?.fullName
          || [selectedCustomer?.firstName, selectedCustomer?.lastName].filter(Boolean).join(' ')
          || id;

        return {
          id,
          fullName,
          requiresCustomerDetails: !selectedCustomer
        };
      });

      this.store.dispatch(new meet.AddIndividualIncludeCustomer({
        page: AppConstants.meetPage,
        customers: includeCustomers,
        replaceIncludedCustomers: true
      }));

      this.dialog.open(SaveGroupNameDialogComponent);
    });
  }

  private fetchCustomersByIds(ids: string[]): Observable<any[]> {
    return this.us.getSelectedLocation().pipe(
      take(1),
      switchMap((location) => this.customerService.getCustomerProfiles(location.locationNumber, ids)),
      map((profiles = []) => profiles.map((profile) => ({
        id: profile.cfaID,
        firstName: profile.firstName,
        lastName: profile.lastName,
        fullName: [profile.firstName, profile.lastName].filter(Boolean).join(' ')
      }))),
      catchError(() => of([]))
    );
  }

  public engageWithCustomer = (engagementIndex: number): void => {
    let payload = {};

    if (this.keepAppliedFilterAnalytics) {
      this.store.select(fromRoot.getMeetSelectedFilters).pipe(take(1)).subscribe(filters => {
        payload = {
          numberOfAppliedFilters: filters.length,
          includedFilters: filters.map(filter=>filter.id).toString(),
        };
        this.analytics.sendEvent('Start New Engage Flow From Action Menu', payload);

        this.store.dispatch(new CustomersSelectedForEngage([this.selectedCustomerIds, filters]));
      })
    } else {

      this.analytics.sendEvent('Start New Engage Flow From Action Menu', payload);

      this.store.dispatch(new CustomersSelectedForEngage([this.selectedCustomerIds, [] ]));
    }

    this.router.navigate(
      ['/engage/template-selection'],
      { queryParams: { retainSelected: true, engagementIndex } }
    );
  }

  public makeInactive() {
    this.analytics.sendEvent(
      'Customers Moved to Inactive',
      { numberOfCustomers: this.selectedCustomerIds.length }
    );
    this.store.dispatch(new customer.MakeInactiveAction(this.selectedCustomerIds));
  }

  public makeActive() {
    this.analytics.sendEvent(
      'Customers Moved to Active',
      { numberOfCustomers: this.selectedCustomerIds.length }
    );
    this.store.dispatch(new customer.MakeActiveAction(this.selectedCustomerIds));
  }

  // If there is an open note with unsaved changes, open confirmation dialog
  // This will accept a function as an input that will be called based on confirmation dialog status
  public checkForUnsavedChanges(userAction: (param?: unknown) => void, optionalParam?: unknown): void {
    if (this.shouldDisplayUnsavedChangesDialog) {
      let config: MatDialogConfig = {
        data: {
          title: this.unsavedChangesTitle,
          displayText: this.unsavedChangesText,
          iconUrl: this.unsavedChangesIcon,
          cancelText: this.unsavedChangesCancel,
          confirmText: this.unsavedChangesConfirm
        },
        autoFocus: false
      }
      config.panelClass = 'dialog-responsive';
      this.dialog.open(IconConfirmationDialogComponent, config).afterClosed().subscribe((shouldCancel) => {
        // Specifically check for false since clicking outside of dialog will return undefined
        let shouldDiscardChanges = shouldCancel === false;
        this.analytics.sendEvent(
          'Unsaved Changes Closed',
          { changesDiscarded: shouldDiscardChanges }
        );
        if (shouldDiscardChanges) {
          userAction(optionalParam);
        }
      });
    } else {
      userAction(optionalParam);
    }
  }

  isOpened() {
    this.menuOpen = true;
  }

  isClosed() {
    this.menuOpen = false;
  }
}
